/**
 * The privacy store — one module-level source of truth for V5-02/V5-03/V5-04.
 *
 * WHY A MODULE-LEVEL STORE AND NOT A REACT CONTEXT
 * -------------------------------------------------
 * Amounts do not reach the screen through a component. They reach it through
 * `formatINR(paise)` — a plain function called inline in JSX in 71 files, plus
 * a couple of dozen local `Intl.NumberFormat` helpers. There is no single
 * `<Amount>` element to wrap, and editing 71 screens to add one would be a
 * codemod, not a feature.
 *
 * So the switch lives where the formatters can see it: a module-level flag,
 * read synchronously by `formatINR` and friends. React context cannot be read
 * from a plain function, `localStorage` cannot be read on every format call
 * without cost, and a prop cannot reach a function. A module flag can.
 *
 * `useSyncExternalStore` gives React the subscription half, so a component
 * that wants to re-render on a toggle can, and `usePrivacyRevision()` mounted
 * at the router root is what makes the whole tree repaint when the flag flips
 * (see `App.tsx`).
 *
 * WHAT IS PERSISTED WHERE, AND WHY BOTH
 * --------------------------------------
 * `localStorage` is the FIRST-PAINT copy. It is read at module load, before
 * React mounts, which is what makes hide-values survive a reload without a
 * flash of visible amounts — the same trick the no-flash theme script in
 * `index.html` uses, for the same reason. Losing that race means the one
 * moment the setting exists to protect (someone reloading the page in a café)
 * is the one moment it does not work.
 *
 * The SERVER copy (`rent.user_preference`, via `/py-api/v1/privacy`) is the
 * durable, cross-device one. `PrivacyProvider` fetches it on sign-in and
 * reconciles. localStorage is a cache of a server value, never the authority.
 *
 * SCOPE
 * -----
 * Strictly per-user. There is no family-wide arm and no `?all=true`, on either
 * side of the wire. An admin's privacy settings are their own and nobody
 * else's.
 */
import { useSyncExternalStore } from "react";

export interface PrivacySettings {
  /** Blur/mask every monetary amount app-wide (V5-03). */
  hideValues: boolean;
  /** Render account and loan numbers as `•••• 4521` (V5-02). */
  maskAccountNumbers: boolean;
  /** Require a password re-prompt before revealing a masked number (V5-04). */
  revealRequiresPassword: boolean;
  /** How long one successful re-auth keeps reveals unlocked, in seconds. */
  revealTimeoutSeconds: number;
}

/**
 * Mirrors `PrivacyPreference` in `backend/api/app/schemas/privacy.py`.
 *
 * `hideValues` off, `maskAccountNumbers` on — deliberately opposite, and the
 * server agrees. Glance-protection that is on by default trains people to
 * switch it off; number masking that is off by default is never switched on.
 */
export const DEFAULT_PRIVACY: PrivacySettings = {
  hideValues: false,
  maskAccountNumbers: true,
  revealRequiresPassword: true,
  revealTimeoutSeconds: 60,
};

export const PRIVACY_STORAGE_KEY = "aim-privacy";

/** Matches `MIN/MAX_REVEAL_TIMEOUT` in `schemas/privacy.py`. */
const MIN_TIMEOUT = 15;
const MAX_TIMEOUT = 900;

function clampTimeout(value: unknown): number {
  const n = typeof value === "number" && Number.isFinite(value) ? Math.round(value) : NaN;
  if (Number.isNaN(n)) return DEFAULT_PRIVACY.revealTimeoutSeconds;
  return Math.min(MAX_TIMEOUT, Math.max(MIN_TIMEOUT, n));
}

/**
 * Coerce an untrusted blob (localStorage, or a server response from a version
 * that did not have all four fields) into a complete settings object.
 * Forgiving by design, exactly like `normalize_privacy` server-side: a bad
 * value falls back to its default rather than locking someone out of their own
 * settings screen.
 */
export function normalizePrivacy(raw: unknown): PrivacySettings {
  if (!raw || typeof raw !== "object") return { ...DEFAULT_PRIVACY };
  const r = raw as Record<string, unknown>;
  const bool = (key: keyof PrivacySettings, fallback: boolean) =>
    typeof r[key] === "boolean" ? (r[key] as boolean) : fallback;

  return {
    hideValues: bool("hideValues", DEFAULT_PRIVACY.hideValues),
    maskAccountNumbers: bool("maskAccountNumbers", DEFAULT_PRIVACY.maskAccountNumbers),
    revealRequiresPassword: bool(
      "revealRequiresPassword",
      DEFAULT_PRIVACY.revealRequiresPassword,
    ),
    revealTimeoutSeconds: clampTimeout(r.revealTimeoutSeconds),
  };
}

function readStored(): PrivacySettings {
  if (typeof localStorage === "undefined") return { ...DEFAULT_PRIVACY };
  try {
    const raw = localStorage.getItem(PRIVACY_STORAGE_KEY);
    return raw ? normalizePrivacy(JSON.parse(raw)) : { ...DEFAULT_PRIVACY };
  } catch {
    return { ...DEFAULT_PRIVACY };
  }
}

/**
 * Read at MODULE LOAD, not in an effect. By the time the first component
 * renders, `hideValues` is already correct — which is the whole point.
 */
let state: PrivacySettings = readStored();

/**
 * Bumped on every change. React's `useSyncExternalStore` needs a snapshot that
 * is referentially stable between changes; returning `state` itself would work
 * too, but a revision number lets a component subscribe to "something changed"
 * without pulling the whole object into its render, which is what the
 * app-wide repaint in `App.tsx` wants.
 */
let revision = 0;

const listeners = new Set<() => void>();

/**
 * Mirror `hideValues` onto `<html data-hide-values>`.
 *
 * The inline script in `index.html` sets this before first paint; this keeps it
 * true afterwards. Nothing in the app styles off it today — masking is done in
 * the formatters, which is what reaches the screens that predate this feature —
 * but it means the state is visible in the elements panel without opening a
 * React devtools inspector, and it gives CSS a hook if a future surface needs
 * one.
 */
function reflectToRoot(): void {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  if (state.hideValues) root.setAttribute("data-hide-values", "true");
  else root.removeAttribute("data-hide-values");
}

function emit(): void {
  revision += 1;
  reflectToRoot();
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * The synchronous read the formatters use. NOT a hook — `formatINR` is a plain
 * function and this is how it learns the flag.
 */
export function getPrivacy(): PrivacySettings {
  return state;
}

/** True when amounts should be masked. The hot path; kept trivial. */
export function shouldHideValues(): boolean {
  return state.hideValues;
}

function persist(next: PrivacySettings): void {
  try {
    localStorage.setItem(PRIVACY_STORAGE_KEY, JSON.stringify(next));
  } catch {
    // Private-browsing quota errors must not break the toggle. The setting
    // still applies for this session; it just will not survive a reload.
  }
}

/**
 * Apply settings locally. Does NOT talk to the server — `PrivacyProvider` owns
 * that, so this stays usable from tests and from the boot path where the
 * server copy has not arrived yet.
 */
export function setPrivacy(patch: Partial<PrivacySettings>): PrivacySettings {
  const next = normalizePrivacy({ ...state, ...patch });
  const unchanged =
    next.hideValues === state.hideValues &&
    next.maskAccountNumbers === state.maskAccountNumbers &&
    next.revealRequiresPassword === state.revealRequiresPassword &&
    next.revealTimeoutSeconds === state.revealTimeoutSeconds;
  if (unchanged) return state;

  state = next;
  persist(next);
  emit();
  return state;
}

/** The one-tap action. Returns the new value so a caller can toast it. */
export function toggleHideValues(): boolean {
  return setPrivacy({ hideValues: !state.hideValues }).hideValues;
}

/**
 * Reset to defaults and forget the cached copy — called on sign-out so the
 * next person to use this browser does not inherit the previous user's
 * settings. Privacy settings are per USER, and localStorage is per BROWSER;
 * this is the seam between the two.
 */
export function resetPrivacy(): void {
  try {
    localStorage.removeItem(PRIVACY_STORAGE_KEY);
  } catch {
    /* see persist() */
  }
  state = { ...DEFAULT_PRIVACY };
  emit();
}

/** Live settings for a component that renders them (the settings screen). */
export function usePrivacy(): PrivacySettings {
  return useSyncExternalStore(subscribe, getPrivacy, getPrivacy);
}

/**
 * Subscribe to "the privacy flag changed" without reading the value.
 *
 * Mounted once, in `App.tsx`, ABOVE the route tree. When the flag flips this
 * re-renders `App`, which recreates the `<Routes />` element, which makes
 * `useRoutes` rebuild the matched elements — and every screen repaints with
 * the formatters now returning masked strings. That is how one toggle reaches
 * 71 files without any of them importing anything.
 *
 * It must be `App` (or a component that re-creates the route element). A
 * provider that receives `children` as a prop cannot do this: `children` is a
 * stable element reference, so React bails out of re-rendering that subtree.
 */
export function usePrivacyRevision(): number {
  return useSyncExternalStore(
    subscribe,
    () => revision,
    () => revision,
  );
}
